import { useCallback, useContext, useEffect, useState } from "react";
import { base, brandInfo, config, sortOption } from "../../../../constant";
import { useLocale, useService } from "../../../../hooks";
import { AppContext } from "../../../../context";

export const useShop = () => {
  const { t } = useLocale();
  const { commonPageSize } = config;
  const { setTitle, setDescription, setKeywords } = useService("Meta");
  const { isValidJson } = useService("Validation");
  const { actions: { showPreloader, hidePreloader } } = useContext(AppContext);
  const {
    isEmpty,
    toIndianCurrency,
    showAlert,
    generateUID,
    toggleBodyClass,
    isBottom,
  } = useService("Misc");
  const { urlToSplitData, getLocationData } = useService("Router");
  const { addToCart: pushToCart } = useService("Order");
  const { getProduct, getProductCategory, getProductBrand } =
    useService("Product");

  const [derivedCategory, setderivedCategory] = useState<string>("");
  const [derivedTag, setderivedTag] = useState<string>("");
  const [productList, setproductList] = useState([]);
  const [filteredProductList, setfilteredProductList] = useState([]);
  const [selectedCategory, setselectedCategory] = useState([]);
  const [selectedBrands, setselectedBrands] = useState([]);
  const [filterCategoryList, setfilterCategoryList] = useState([]);
  const [filterBrandList, setfilterBrandList] = useState([]);
  const [activeShownTotal, setactiveShownTotal] =
    useState<number>(commonPageSize);
  const [selectedFilter, setselectedFilter] = useState(sortOption[1]);
  const [filterTags, setfilterTags] = useState([]);
  const [filteredPrice, setfilteredPrice] = useState(50000);
  const [maxPrice] = useState(50000);
  const [minPrice] = useState<number>(0);

  const compare = useCallback((a, b, key = "title") => {
    if (a[key] < b[key]) return -1;
    if (a[key] > b[key]) return 1;
    return 0;
  }, []);

  const compareByPrice = useCallback((a, b) => {
    if (Number(a.price) < Number(b.price)) return -1;
    if (Number(a.price) > Number(b.price)) return 1;
    return 0;
  }, []);

  const getProductCategoryData = useCallback(async () => {
    const result = await getProductCategory();
    if (result.data?.length !== 0) setfilterCategoryList(result.data);
  }, [getProductCategory]);

  const getProductBrandData = useCallback(async () => {
    const result = await getProductBrand();
    if (result.data?.length !== 0) {
      setfilterBrandList(result.data);
    }
  }, [getProductBrand]);

  const getProductsData = useCallback(async () => {
    const result = await getProduct({ status: "publish" });
    if (result.status) {
      if (result.data?.length > 0) {
        let newProductList = result.data.reverse(),
          tempProductList = [],
          tableData = [];
        newProductList.forEach((e) => {
          let found = false;
          if (derivedTag !== "") found = e.tag?.includes(derivedTag);
          else if (derivedCategory !== "" && isValidJson(e.category)) {
            JSON.parse(e.category).forEach((c) => {
              if (c.value === derivedCategory) found = true;
            });
          } else found = true;
          if (found) tempProductList.push(e);
        });

        tempProductList.forEach((e) => {
          if (!e.category) {
            e.category = e.categories;
          }
          if (typeof e.trash === "undefined") tableData.push(e);
          else if (!e.trash) tableData.push(e);
        });
        setproductList(tableData);
        setfilteredProductList(tableData);
      }
    }
  }, [getProduct, derivedTag, derivedCategory, isValidJson]);

  const showMore = useCallback(() => {
    showPreloader();
    setTimeout(() => {
      hidePreloader();
      setactiveShownTotal((prev) => prev + 24);
    }, 500);
  }, [hidePreloader, showPreloader]);

  const trackScrolling = useCallback(() => {
    const wrappedElement = document.getElementById("list-wrapper");
    if (isBottom(wrappedElement)) {
      if (
        filteredProductList.length > 24 &&
        activeShownTotal < filteredProductList.length
      )
        showMore();
    }
  }, [activeShownTotal, filteredProductList.length, isBottom, showMore]);

  useEffect(() => {
    setTitle(
      t("shop.meta.title").replace("{brand}", brandInfo.name.capital),
      "empty"
    );
    setDescription(
      t("shop.meta.description").replace(/{brand}/g, brandInfo.name.capital)
    );
    setKeywords(
      t("shop.meta.keywords").replace(/{brand}/g, brandInfo.name.capital)
    );
    const urlData = urlToSplitData(getLocationData().pathname);
    if (urlData[3]) setderivedTag(urlData[3]);
    else if (urlData[2]) setderivedCategory(urlData[2]);
    getProductCategoryData();
    getProductBrandData();
    getProductsData();
    document.addEventListener("scroll", trackScrolling);
    return () => {
      document.removeEventListener("scroll", trackScrolling);
    };
  }, [
    t,
    setTitle,
    setDescription,
    setKeywords,
    getLocationData,
    urlToSplitData,
    getProductCategoryData,
    getProductBrandData,
    getProductsData,
    trackScrolling,
  ]);

  const applyFilters = useCallback((categories, brands, price) => {
    let newFilteredProductList = [];
    productList.forEach((e) => {
      let matchesCategory = true;
      let matchesBrand = true;
      let matchesPrice = true;
      let matchesDerived = true;

      // 1. Derived Tag/Category from URL
      if (derivedTag !== "") {
        matchesDerived = e.tag?.includes(derivedTag);
      } else if (derivedCategory !== "" && isValidJson(e.category)) {
        let foundCat = false;
        JSON.parse(e.category).forEach((c) => {
          if (c.value === derivedCategory) foundCat = true;
        });
        matchesDerived = foundCat;
      }

      // 2. Category Filter
      if (categories.length > 0) {
        let foundCat = false;
        if (isValidJson(e.category)) {
          JSON.parse(e.category).forEach((c) => {
            categories.forEach((sc) => {
              if (c.value === sc.value) foundCat = true;
            });
          });
        }
        matchesCategory = foundCat;
      }

      // 3. Brand Filter
      if (brands.length > 0) {
        let foundBrand = false;
        if (!isEmpty(e.brand)) {
          let brandObj = JSON.parse(e.brand);
          if (brandObj && brandObj.value) {
            brands.forEach((sb) => {
              if (sb.value === brandObj.value) foundBrand = true;
            });
          }
        }
        matchesBrand = foundBrand;
      }

      // 4. Price Filter
      if (Number(e.price) > Number(price)) {
        matchesPrice = false;
      }

      if (matchesDerived && matchesCategory && matchesBrand && matchesPrice) {
        newFilteredProductList.push(e);
      }
    });

    // Apply sorting if needed
    if (selectedFilter.value === "name") newFilteredProductList.sort(compare);
    else newFilteredProductList.sort(compareByPrice);

    setfilteredProductList(newFilteredProductList);
  }, [productList, derivedTag, derivedCategory, isValidJson, isEmpty, selectedFilter, compare, compareByPrice]);

  const handleChange = (selectedFilter) => {
    setselectedFilter(selectedFilter);
    if (selectedFilter.value === "name") filteredProductList.sort(compare);
    else filteredProductList.sort(compareByPrice);
    setfilteredProductList(filteredProductList);
  };

  const productAddedTemplate = useCallback((item) => {
    let prodcutAddedTemplate = `<div class="alert-msg"><i class="hi-checkbox-checked"></i><p>${t(
      "success.addToCart.product"
    )}</p></div>
        <div class="product-box">
        <img src="${base.imageBaseUrl + item.image}" alt="${
      item.title
    }" class="product-image" />'+
        <div class="product-content">
        <p class="product-title">${item.title}</p>
        <p class="product-price">${toIndianCurrency(item.displayPrice)}</p>
        </div>
        </div>`;
    showAlert({ type: "custom", template: prodcutAddedTemplate });
  }, [t, toIndianCurrency, showAlert]);

  const addToCart = useCallback((item) => {
    let price = item.price,
      mrp = price,
      saleprice = item.saleprice;
    if (
      item.saleprice &&
      item.saleprice !== 0 &&
      item.saleprice !== "" &&
      item.saleprice !== item.mrp
    )
      price = saleprice;
    const taxAmount = Number(
      ((price * Number(item.tax)) / (100 + Number(item.tax))).toFixed(2)
    );
    let product = {
      uid: item.uid,
      title: item.title,
      image: JSON.parse(item.image)[0],
      purchaseQuantity: 1,
      type: "product",
      tempUid: generateUID(),
      mrp: Number(mrp),
      saleprice,
      tax: item.tax,
      hsn: item.hsn,
      sku: item.sku,
      taxAmount,
      price: price - taxAmount,
      displayPrice: price,
    };
    pushToCart(product);
    productAddedTemplate(product);
  }, [generateUID, productAddedTemplate, pushToCart]);

  const toggleSideFilter = useCallback(() => {
    toggleBodyClass("nav-open-filter");
  }, [toggleBodyClass]);

  const toggleSelectedCategory = useCallback((category, action) => {
    let derivedSelectedCategory = [...selectedCategory];
    let derivedFilterTags = [...filterTags];

    if (action === "push") {
      let cat = {
        label: category.catName,
        value: category.catSlug,
        type: "category",
      };
      derivedSelectedCategory.push(cat);
      derivedFilterTags.push(cat);
    } else {
      let slug = category.catSlug || category.value;
      derivedSelectedCategory = derivedSelectedCategory.filter(c => c.value !== slug);
      derivedFilterTags = derivedFilterTags.filter(t => t.value !== slug);
    }

    setselectedCategory(derivedSelectedCategory);
    setfilterTags(derivedFilterTags);
    applyFilters(derivedSelectedCategory, selectedBrands, filteredPrice);
  }, [selectedCategory, filterTags, applyFilters, selectedBrands, filteredPrice]);

  const toggleSelectedBrands = useCallback((brands, action) => {
    let derivedSelectedBrands = [...selectedBrands];
    let derivedFilterTags = [...filterTags];

    if (action === "push") {
      let brand = {
        label: brands.brandName,
        value: brands.brandSlug,
        type: "brand",
      };
      derivedSelectedBrands.push(brand);
      derivedFilterTags.push(brand);
    } else {
      let slug = brands.brandSlug || brands.value;
      derivedSelectedBrands = derivedSelectedBrands.filter(b => b.value !== slug);
      derivedFilterTags = derivedFilterTags.filter(t => t.value !== slug);
    }

    setselectedBrands(derivedSelectedBrands);
    setfilterTags(derivedFilterTags);
    applyFilters(selectedCategory, derivedSelectedBrands, filteredPrice);
  }, [selectedBrands, filterTags, applyFilters, selectedCategory, filteredPrice]);

  const filterByPrice = useCallback((price) => {
    setfilteredPrice(price);
    applyFilters(selectedCategory, selectedBrands, price);
  }, [applyFilters, selectedCategory, selectedBrands]);

  let pageTitle =
    derivedCategory === "" ? "Accessories" : derivedCategory.replace(/-/g, " ");

  return {
    activeShownTotal,
    addToCart,
    selectedCategory,
    toggleSelectedCategory,
    derivedCategory,
    selectedBrands,
    toggleSelectedBrands,
    pageTitle,
    filterByPrice,
    minPrice,
    maxPrice,
    filteredPrice,
    filterCategoryList,
    filterBrandList,
    derivedTag,
    filterTags,
    toggleSideFilter,
    selectedFilter,
    handleChange,
    filteredProductList,
  };
};